// src/components/artists/ArtistMetricsTables.tsx - Mit wachsender Höhe
import React from 'react';
import {
  MinusIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';
import Tooltip from '../common/Tooltip';

interface ProcessedMetric {
  month: string;
  streams: number;
  listeners: number;
  saves: number;
  followers: number;
  playlists: number;
  score: number;
  saveRate: number;
  streamsChange: number | null;
  listenersChange: number | null;
  savesChange: number | null;
  playlistsChange: number | null;
  scoreChange: number | null;
}

interface ArtistMetricsTablesProps {
  metrics: ProcessedMetric[];
  selectedYear: number;
}

export const ArtistMetricsTables: React.FC<ArtistMetricsTablesProps> = ({ 
  metrics, 
  selectedYear 
}) => {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-700">Monthly Metrics</h2>
      </div>

      <div className="overflow-auto" style={{ minHeight: "50px", maxHeight: "400px" }}>
        {metrics.length > 0 ? (
          <table className="min-w-full">
            <thead className="bg-white sticky top-0">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Streams</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Listeners</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Saves</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    <span>Playlists</span>
                    <Tooltip text="Number of playlists that added your songs during this month." />
                  </div>
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {metrics.map((metric, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  {/* Month */}
                  <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                    {metric.month.replace(/\s\d{4}$/, '')}
                  </td>
          
                  {/* Streams */}
                  <td className="px-4 py-2 whitespace-nowrap text-sm">
                    <div className="flex items-center">
                      <span className="text-gray-900">{metric.streams.toLocaleString()}</span>
                      {metric.streamsChange !== null && (
                        <span className={`ml-2 flex items-center text-xs ${metric.streamsChange > 0 ? 'text-green-600' : metric.streamsChange < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                          {metric.streamsChange === 0 ? <MinusIcon className="h-4 w-4 mr-1" /> :
                           metric.streamsChange > 0 ? <ArrowTrendingUpIcon className="h-4 w-4 mr-1" /> :
                           <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />}
                          {Math.abs(metric.streamsChange).toFixed(1)}%
                        </span>
                      )}
                    </div>
                  </td>
          
                  {/* Listeners */}
                  <td className="px-4 py-2 whitespace-nowrap text-sm">
                    <div className="flex items-center">
                      <span className="text-gray-900">{metric.listeners.toLocaleString()}</span>
                      {metric.listenersChange !== null && (
                        <span className={`ml-2 flex items-center text-xs ${metric.listenersChange > 0 ? 'text-green-600' : metric.listenersChange < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                          {metric.listenersChange === 0 ? <MinusIcon className="h-4 w-4 mr-1" /> :
                           metric.listenersChange > 0 ? <ArrowTrendingUpIcon className="h-4 w-4 mr-1" /> :
                           <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />}
                          {Math.abs(metric.listenersChange).toFixed(1)}%
                        </span>
                      )}
                    </div>
                  </td>
          
                  {/* Saves */}
                  <td className="px-4 py-2 whitespace-nowrap text-sm">
                    <div className="flex items-center">
                      <span className="text-gray-900">{metric.saves.toLocaleString()}</span>
                      {metric.savesChange !== null && (
                        <span className={`ml-2 flex items-center text-xs ${metric.savesChange > 0 ? 'text-green-600' : metric.savesChange < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                          {metric.savesChange === 0 ? <MinusIcon className="h-4 w-4 mr-1" /> :
                           metric.savesChange > 0 ? <ArrowTrendingUpIcon className="h-4 w-4 mr-1" /> :
                           <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />}
                          {Math.abs(metric.savesChange).toFixed(1)}%
                        </span>
                      )}
                    </div>
                  </td>
          
                  {/* Playlists */}
                  <td className="px-4 py-2 whitespace-nowrap text-sm">
                    <div className="flex items-center">
                      <span className="text-gray-900">{metric.playlists.toLocaleString()}</span>
                      {metric.playlistsChange !== null && (
                        <span className={`ml-2 flex items-center text-xs ${metric.playlistsChange > 0 ? 'text-green-600' : metric.playlistsChange < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                          {metric.playlistsChange === 0 ? <MinusIcon className="h-4 w-4 mr-1" /> :
                           metric.playlistsChange > 0 ? <ArrowTrendingUpIcon className="h-4 w-4 mr-1" /> :
                           <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />}
                          {Math.abs(metric.playlistsChange).toFixed(1)}%
                        </span>
                      )}
                    </div>
                  </td>
          
                  {/* Score */}
                  <td className="px-4 py-2 whitespace-nowrap text-sm">
                    <div className="flex items-center">
                      <span className="text-gray-900">{metric.score.toFixed(2)}</span>
                      {metric.scoreChange !== null && (
                        <span className={`ml-2 flex items-center text-xs ${metric.scoreChange > 0 ? 'text-green-600' : metric.scoreChange < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                          {metric.scoreChange === 0 ? <MinusIcon className="h-4 w-4 mr-1" /> :
                           metric.scoreChange > 0 ? <ArrowTrendingUpIcon className="h-4 w-4 mr-1" /> :
                           <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />}
                          {Math.abs(metric.scoreChange).toFixed(2)} pts
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-6 text-gray-500">
            No metrics available for {selectedYear}
          </div>
        )}
      </div>
    </div>
  );
};